import { QueryFactory } from '@lmt/shared-types';
import {
  CreateDictionaryRecordDto,
  CreateOptionalSubtenantDictionaryRecordDto,
  DictionaryRecordDto,
  OptionalSubtenantDictionaryRecordDto,
  UpdateDictionaryRecordDto,
  UpdateOptionalSubtenantDictionaryRecordDto,
} from '@lmt/shared-types';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';
import { QueryClient, UseQueryResult } from '@tanstack/react-query';

export type DictionaryInfo = {
  name: string;
  maxSize: number;
  root: string;
  prefix: string;
  remove: (id: number) => Promise<void>;
  invalidate?: (queryClient: QueryClient) => Promise<void>;
  removeUnused?: () => Promise<void>;

  isOptionalSubtenant: boolean;
} & (
  | {
      isOptionalSubtenant: true;
      create: (
        data: CreateOptionalSubtenantDictionaryRecordDto,
      ) => Promise<OptionalSubtenantDictionaryRecordDto>;
      edit: (
        id: number,
        data: UpdateOptionalSubtenantDictionaryRecordDto,
      ) => Promise<OptionalSubtenantDictionaryRecordDto>;
      getData: () => UseQueryResult<
        OptionalSubtenantDictionaryRecordDto[],
        unknown
      >;
    }
  | {
      isOptionalSubtenant: false;
      create: (data: CreateDictionaryRecordDto) => Promise<DictionaryRecordDto>;
      edit: (
        id: number,
        data: UpdateDictionaryRecordDto,
      ) => Promise<DictionaryRecordDto>;
      getData: () => UseQueryResult<
        OptionalSubtenantDictionaryRecordDto[],
        unknown
      >;
    }
);

export const dictionaryKeys: { [dictionary: string]: DictionaryInfo } = {
  tags: {
    name: 'tags',
    maxSize: 250,
    root: 'tags',
    prefix: 'tag.',
    isOptionalSubtenant: false,
    remove: async (id: number) => await ClientFactory.TagClient.remove(id),
    removeUnused: async () => ClientFactory.TagClient.removeUnused(),
    create: async (data: CreateDictionaryRecordDto) =>
      await ClientFactory.TagClient.create(data),
    edit: async (id: number, data: UpdateDictionaryRecordDto) =>
      await ClientFactory.TagClient.edit(id, data),
    getData: () => {
      return QueryFactory.TagQuery.useGetQuery();
    },
    async invalidate(queryClient) {
      await queryClient.refetchQueries({
        queryKey: QueryFactory.TagQuery.getQueryKey(),
      });
      await queryClient.refetchQueries({
        queryKey:
          QueryFactory.PatientListConfigurationQuery.getPatientListConfigurationQueryKey(
            undefined!,
          ),
      });
      await queryClient.refetchQueries({
        queryKey: QueryFactory.PatientCommentQuery.getQueryKey(undefined!),
      });
      await queryClient.refetchQueries({
        queryKey:
          QueryFactory.CalendarConfigurationQuery.getCalendarConfigurationQueryKey(
            undefined!,
          ),
      });
    },
  },
};

export const dictionaryInfos = [dictionaryKeys.tags];
